import * as Yup from 'yup';
import { InputFieldProps } from 'components/Forms';
import { getRequiredErrorMessageNew } from 'constants/validation';
import { GeographicArea } from 'models/GeographicArea';
import CapacityType from 'models/CapacityType';
import { DateTime } from 'luxon';

export enum EditReserveCapacityFormFields {
  Area = 'editReserveCapacityArea',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  CapacityType = 'editReserveCapacityCapacityType',
  ValidFrom = 'editReserveCapacityValidFrom',
  ValidTo = 'editReserveCapacityValidTo',
  Percentage = 'editReserveCapacityCapacityPercentage',
  ReleaseAt = 'editReserveCapacityReleaseAt',
  EffectiveDays = 'editReserveCapacityWeekdays'
}

const percentageOptions = Array.from({ length: 100 }, (_, i) => ({
  label: `${i + 1}%`,
  value: `${i + 1}`
}));

const releaseAtOptions = Array.from({ length: 50 }, (_, i) => ({
  label: `${i + 1} (day)`,
  value: `${i + 1}`
}));

export const getEditReserveCapacityFields = (
  areas?: GeographicArea[],
  capacityTypes?: CapacityType[]
): InputFieldProps[] => [
  {
    id: EditReserveCapacityFormFields.Area,
    required: true,
    name: EditReserveCapacityFormFields.Area,
    qaId: EditReserveCapacityFormFields.Area,
    inputType: 'singleselect',
    label: 'Area',
    options:
      areas?.map(({ name, id }) => ({
        label: name,
        value: id
      })) ?? []
  },
  {
    id: EditReserveCapacityFormFields.CapacityType,
    required: true,
    name: EditReserveCapacityFormFields.CapacityType,
    qaId: EditReserveCapacityFormFields.CapacityType,
    inputType: 'singleselect',
    label: 'Capacity type',
    options:
      capacityTypes?.map(({ name, id }) => ({
        label: name,
        value: id
      })) ?? []
  },
  {
    id: EditReserveCapacityFormFields.ValidFrom,
    required: true,
    name: EditReserveCapacityFormFields.ValidFrom,
    qaId: EditReserveCapacityFormFields.ValidFrom,
    inputType: 'dateNew',
    label: 'Valid From'
  },
  {
    id: EditReserveCapacityFormFields.ValidTo,
    required: true,
    name: EditReserveCapacityFormFields.ValidTo,
    qaId: EditReserveCapacityFormFields.ValidTo,
    inputType: 'dateNew',
    label: 'Valid To'
  },
  {
    id: EditReserveCapacityFormFields.Percentage,
    required: true,
    name: EditReserveCapacityFormFields.Percentage,
    qaId: EditReserveCapacityFormFields.Percentage,
    inputType: 'singleselect',
    label: '% Capacity',
    options: percentageOptions
  },
  {
    id: EditReserveCapacityFormFields.ReleaseAt,
    required: true,
    name: EditReserveCapacityFormFields.ReleaseAt,
    qaId: EditReserveCapacityFormFields.ReleaseAt,
    inputType: 'singleselect',
    label: 'Release at',
    options: releaseAtOptions
  },
  {
    id: EditReserveCapacityFormFields.EffectiveDays,
    required: true,
    name: EditReserveCapacityFormFields.EffectiveDays,
    qaId: EditReserveCapacityFormFields.EffectiveDays,
    inputType: 'weekdays',
    label: 'Effective Days'
  }
];

export function sanitizeDateForComparison(date: string) {
  const dateISO = DateTime.fromISO(date);
  if (dateISO.isValid) {
    return dateISO;
  }
  return DateTime.fromFormat(date, 'dd/MM/yyyy');
}

export const validationSchema = Yup.object().shape(
  {
    [EditReserveCapacityFormFields.Area]: Yup.string().required(
      getRequiredErrorMessageNew('Area', true)
    ),
    [EditReserveCapacityFormFields.CapacityType]: Yup.string().required(
      getRequiredErrorMessageNew('Capacity Type', true)
    ),
    [EditReserveCapacityFormFields.ValidFrom]: Yup.string()
      .required(getRequiredErrorMessageNew('Date', true))
      .nullable()
      .when(
        EditReserveCapacityFormFields.ValidTo,
        (validUntil: string, schema) =>
          schema.test({
            test: (validFrom: string) => {
              if (validFrom && validUntil) {
                return (
                  sanitizeDateForComparison(validFrom).diff(
                    sanitizeDateForComparison(validUntil)
                  ).milliseconds <= 0
                );
              }
              return true;
            },
            message: 'Valid from date must be before valid to date'
          })
      ),
    [EditReserveCapacityFormFields.ValidTo]: Yup.string()
      .required(getRequiredErrorMessageNew('Date', true))
      .nullable()
      .when(
        EditReserveCapacityFormFields.ValidFrom,
        (validFrom: string, schema) =>
          schema.test({
            test: (validUntil: string) => {
              if (validFrom && validUntil) {
                return (
                  sanitizeDateForComparison(validFrom).diff(
                    sanitizeDateForComparison(validUntil)
                  ).milliseconds <= 0
                );
              }
              return true;
            },
            message: 'Valid to date must be after valid from date'
          })
      )
      .test('is-not-past', 'Valid until cannot be in the past', value => {
        if (!value) {
          return true;
        }
        return (
          sanitizeDateForComparison(value).startOf('day') >=
          DateTime.local().startOf('day')
        );
      }),
    [EditReserveCapacityFormFields.Percentage]: Yup.string().required(
      getRequiredErrorMessageNew('Capacity percentage', false)
    ),
    [EditReserveCapacityFormFields.ReleaseAt]: Yup.string().required(
      getRequiredErrorMessageNew('Number of days', false)
    ),
    [EditReserveCapacityFormFields.EffectiveDays]: Yup.object()
      .test('at-least-one-day', 'At least one day must be selected', value => {
        return value && Object.values(value).some(Boolean);
      })
      .required('At least one day must be selected')
      .nullable()
  },
  [
    [
      EditReserveCapacityFormFields.ValidFrom,
      EditReserveCapacityFormFields.ValidTo
    ]
  ]
);
